import {Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {FC, useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {useInitialEffect} from '../../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {center, ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useDebounce} from '../../../../../../srcCommon/helpers/hookHelpers';
import {
  buildSearchToken,
  isWildcardToken,
  SearchItemProps,
  SearchToken,
} from '../../../../../../srcCommon/inboxes/search/searchHelpers';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchTypes';
import {
  buildCancelTokenSource,
  CancelError,
  CancelTokenSource,
} from '../../../../../helpers/asyncCancelHelpers';
import {getTagColorHighlight, getTagEmojiHighlight} from '../../../../../helpers/models/highlightHelpers';
import {isTeammateNamespace} from '../../../../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../../../helpers/reduxHelpers';
import {AccountModel} from '../../../../../models/api/accountModel';
import {
  ApiSmartMixMatches,
  SmartMixClassnamesEnum,
  SmartMixMatchModel,
} from '../../../../../models/api/smartMixMatchModel';
import {TagModel} from '../../../../../models/api/tagModel';
import {TeammateModel} from '../../../../../models/api/teammateModel';
import {fetchApiAsync} from '../../../../../services/http/http';
import {getCompany} from '../../../../../store/data/company/companySelectors';
import {AccountAvatar} from '../../../../common/avatars/accountAvatar';
import {Emoji} from '../../../../common/emoji/emoji';
import {FuzzyHighlight} from '../../../cells/header/search/highlight/fuzzyHighlight';
import {
  InboxesSearchSuggestionsBaseProps,
  InboxesSuggestionsDropdownItem,
  InboxesSuggestionsDropdownItemIcon,
  InboxesSuggestionsDropdownItemPrimaryContent,
  InboxesSuggestionsDropdownItemRecipientAvatar,
  InboxesSuggestionsDropdownItemSecondaryContent,
  InboxesSuggestionsDropdownItemTeammateAvatar,
  InboxesSuggestionsDropdownSection,
  InboxesSuggestionsDropdownSectionTitle,
  InboxesSuggestionsDropdownSectionWrapper,
  SEARCH_DROPDOWN_DEBOUNCE_WAIT_MS,
} from './inboxesSearchSuggestionsDropdownCommons';

/*
 * Props.
 */

interface InboxesSearchSuggestionsDropdownSmartMixProps extends InboxesSearchSuggestionsBaseProps {
  teammates: ReadonlyArray<TeammateModel>;
  namespace?: string;
}

/*
 * Styles.
 */

const StyledNameDiv = styled.div`
  flex: 1;
  ${ellipsis()};
`;

const StyledArchivedBadge = styled.div`
  font-weight: ${fontWeights.bold};
  font-size: ${fontSizes.veryTiny};
  color: ${(p) => p.theme.greys.shade80};
  text-transform: uppercase;
  padding: 3px 5px 3px;
  background-color: ${(p) => p.theme.alphas.gray20};
  border-radius: 100px;
  display: inline-block;
  margin-right: 6px;
  line-height: 1;
  vertical-align: middle;
  margin-top: -1px;
`;

const StyledInboxesSuggestionsDropdownItemPrimaryContent = styled(
  InboxesSuggestionsDropdownItemPrimaryContent,
)`
  display: flex;
`;

const StyledLockIconWrapperDiv = styled.div`
  margin-left: 5px;
  flex-shrink: 0;
  ${center()};
`;

const StyledIconDiv = styled.div`
  grid-area: icon;
  margin-right: 8px;
`;

const StyledInboxesSuggestionsDropdownItemSecondaryContent = styled(
  InboxesSuggestionsDropdownItemSecondaryContent,
)`
  display: inline-block;
`;

/*
 * Component.
 */

export const InboxesSearchSuggestionsDropdownSmartMix: FC<InboxesSearchSuggestionsDropdownSmartMixProps> = ({
  teammates,
  query,
  activeToken,
  namespace,
  onSuggestionSelect,
}) => {
  const company = useAppSelector(getCompany);

  const [items, setItems] = useState<ReadonlyArray<SmartMixMatchModel>>([]);

  const cancelTokenSourceRef = useRef<CancelTokenSource>();
  const maybeCancelRequest = () => {
    const currentCancelTokenSource = cancelTokenSourceRef.current;
    return currentCancelTokenSource && currentCancelTokenSource.cancel();
  };

  useInitialEffect(() => {
    fetchItemsAsync(activeToken);

    return () => maybeCancelRequest();
  });

  const fetchItemsAsync = async (searchToken: SearchToken) => {
    // Cancel any existing request.
    maybeCancelRequest();

    const cancelTokenSource = buildCancelTokenSource();
    cancelTokenSourceRef.current = cancelTokenSource;

    // Conditions to load smart mix
    if (
      isUndefined(searchToken.value) ||
      !isUndefined(searchToken.modifier) ||
      isWildcardToken(searchToken, query) ||
      isInvalidDotToken(searchToken)
    ) {
      return setItems([]);
    }

    const {token} = cancelTokenSource;
    const encodedQuery = encodeURIComponent(searchToken.value);
    const basePath = company.url;

    const path = `${basePath}/search_suggestions/${encodedQuery}?namespace=${namespace}`;

    try {
      const matches = await fetchApiAsync(ApiSmartMixMatches, path, {cancelToken: token});

      // Only keep first 4 items.
      return setItems(matches.slice(0, 4));
    } catch (e) {
      if (!(e instanceof CancelError)) {
        throw e;
      }

      return null;
    }
  };

  const fetchItemsAsyncDebounced = useDebounce(fetchItemsAsync, SEARCH_DROPDOWN_DEBOUNCE_WAIT_MS);

  useEffect(() => {
    fetchItemsAsyncDebounced(activeToken);
  }, [activeToken, fetchItemsAsyncDebounced]);

  // Return early if there are no items.
  if (!items.length) {
    return null;
  }

  const renderItemContent = (item: SmartMixMatchModel) => {
    if (item.class === SmartMixClassnamesEnum.TAG) {
      return renderTagResource(item.tag);
    }

    if (item.class === SmartMixClassnamesEnum.ACCOUNT) {
      return renderAccountResource(item.account);
    }

    return (
      <>
        {renderSmartMixIcon(item, teammates)}
        <InboxesSuggestionsDropdownItemPrimaryContent>
          <FuzzyHighlight query={activeToken.value} text={item.primaryLabel} />
        </InboxesSuggestionsDropdownItemPrimaryContent>
        <InboxesSuggestionsDropdownItemSecondaryContent>
          <FuzzyHighlight query={activeToken.value} text={item.secondaryLabel} />
        </InboxesSuggestionsDropdownItemSecondaryContent>
      </>
    );
  };

  const onSelect = (item: SmartMixMatchModel, key?: Key) => {
    if (item.class === SmartMixClassnamesEnum.ACCOUNT) {
      onSuggestionSelect(`${ModifiersEnum.ACCOUNT}:${item.value}`, key, {
        insertWhiteSpace: false,
        isSmartMixItem: true,
      });
      return;
    }

    onSuggestionSelect(item.value, key, {
      insertWhiteSpace: shouldInsertWhitespace(item),
      isSmartMixItem: true,
    });
  };

  return (
    <InboxesSuggestionsDropdownSectionWrapper>
      <InboxesSuggestionsDropdownSection>
        <InboxesSuggestionsDropdownSectionTitle>
          <Trans id="inboxes-search-smart-mix">Suggestions</Trans>
        </InboxesSuggestionsDropdownSectionTitle>
        {items.map((item) => (
          <InboxesSuggestionsDropdownItem onSelect={(_e, key) => onSelect(item, key)} key={item.value}>
            {renderItemContent(item)}
          </InboxesSuggestionsDropdownItem>
        ))}
      </InboxesSuggestionsDropdownSection>
    </InboxesSuggestionsDropdownSectionWrapper>
  );
};

/*
 * Helpers.
 */

function renderSmartMixIcon(item: SmartMixMatchModel, teammates: ReadonlyArray<TeammateModel>) {
  switch (item.class) {
    case SmartMixClassnamesEnum.INBOX:
      return <InboxesSuggestionsDropdownItemIcon iconName="inboxSmall" />;

    case SmartMixClassnamesEnum.TEAMMATE: {
      const teammate = teammates.find(({id}) => id === item.teammateId);
      return <InboxesSuggestionsDropdownItemTeammateAvatar teammate={teammate} />;
    }

    case SmartMixClassnamesEnum.CONTACT:
      return <InboxesSuggestionsDropdownItemRecipientAvatar recipient={item.recipient} />;

    case SmartMixClassnamesEnum.MODIFIER:
    case SmartMixClassnamesEnum.PROPERTY:
      return <InboxesSuggestionsDropdownItemIcon iconName="searchSmall" />;

    default:
      return null;
  }
}

function shouldInsertWhitespace(item: SearchItemProps) {
  // Navigate if the selected item contains a value.
  const token = buildSearchToken(item.value);
  return Boolean(token && token.value);
}

export function renderTagResource(tag: TagModel) {
  return (
    <>
      {renderSearchListItemTagHighlight(tag)}
      <UserThemeConsumer>
        {(theme) => (
          <StyledInboxesSuggestionsDropdownItemPrimaryContent>
            <StyledNameDiv>
              {tag.archivedAt && (
                <StyledArchivedBadge>
                  <Trans id="tag-archived">Archived</Trans>
                </StyledArchivedBadge>
              )}
              {tag.name}
            </StyledNameDiv>
            {isTeammateNamespace(tag.namespace) && (
              <StyledLockIconWrapperDiv>
                <Icon name="lockFilledTiny" color={theme.greys.shade60} />
              </StyledLockIconWrapperDiv>
            )}
          </StyledInboxesSuggestionsDropdownItemPrimaryContent>
        )}
      </UserThemeConsumer>
    </>
  );
}

function renderSearchListItemTagHighlight(tag: TagModel) {
  const emojiHighlight = getTagEmojiHighlight(tag);

  if (emojiHighlight) {
    return (
      <StyledIconDiv>
        <Emoji emoji={emojiHighlight.emoji} skinTone={emojiHighlight.skinTone} size={16} />
      </StyledIconDiv>
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => {
        const colorHighlight = getTagColorHighlight(tag);
        const iconColor = colorHighlight && theme.palette[colorHighlight].shade40;
        return (
          <StyledIconDiv>
            <Icon name="tagSmall" color={iconColor || theme.greys.shade70} />
          </StyledIconDiv>
        );
      }}
    </UserThemeConsumer>
  );
}

export function renderAccountResource(account: AccountModel) {
  return (
    <>
      <StyledIconDiv>
        <AccountAvatar account={account} size={16} avatarIconSize={16} />
      </StyledIconDiv>
      <StyledInboxesSuggestionsDropdownItemPrimaryContent>
        <StyledNameDiv>{account.name}</StyledNameDiv>

        <StyledInboxesSuggestionsDropdownItemSecondaryContent>
          {account.domains.join(', ')}
        </StyledInboxesSuggestionsDropdownItemSecondaryContent>
      </StyledInboxesSuggestionsDropdownItemPrimaryContent>
    </>
  );
}

function isInvalidDotToken(token: SearchToken) {
  // When appended to a URL, the browser interprets those as current directory and previous directory.
  // This can lead to HTTP errors, we should skip them.
  return isUndefined(token.modifier) && (token.value === '.' || token.value === '..');
}
